import React, { useState, useEffect } from 'react';
import {
  Card, Table, Select, Button, Space, Tag, Tabs, Tooltip,
  message, InputNumber, Alert, Badge, Typography, Row, Col,
} from 'antd';
import {
  TrophyOutlined, CheckCircleOutlined, WarningOutlined,
  DownloadOutlined, SearchOutlined, StarOutlined,
} from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import { getCicloActual, getAniosAcademicos } from '../../../utils/cicloEscolar';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Option } = Select;
const { Text } = Typography;

const CICLO_ACTUAL = getCicloActual();

// Helpers
const toNum = (v) => (v === null || v === undefined || v === '') ? null : Number(v);

const TagNota = ({ valor, style }) => {
  const n = toNum(valor);
  if (n === null) return <Tag color="default">-</Tag>;
  return <Tag color={n >= 60 ? 'green' : 'red'} style={style}>{n.toFixed(2)}</Tag>;
};

const AÑOS = getAniosAcademicos();

const RendimientoEstudiantil = () => {
  const [tab, setTab] = useState('top');

  // Catálogos
  const [grados, setGrados] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  // ── Top Promedios ────────────────────────────────────────────────────────
  const [filtrosTop, setFiltrosTop] = useState({
    p_CicloEscolar: CICLO_ACTUAL,
    IdGrado: null, IdSeccion: null, IdJornada: null,
    limite: 20,
  });
  const [dataTop, setDataTop] = useState([]);
  const [loadingTop, setLoadingTop] = useState(false);

  // ── En Limpio ────────────────────────────────────────────────────────────
  const [filtrosLimpio, setFiltrosLimpio] = useState({
    p_CicloEscolar: CICLO_ACTUAL,
    IdNivel: null, IdGrado: null, IdSeccion: null, IdJornada: null,
  });
  const [dataLimpio, setDataLimpio] = useState([]);
  const [loadingLimpio, setLoadingLimpio] = useState(false);

  // ── Cursos Perdidos ──────────────────────────────────────────────────────
  const [filtrosPerdidos, setFiltrosPerdidos] = useState({
    p_CicloEscolar: CICLO_ACTUAL,
    IdGrado: null, IdSeccion: null, IdJornada: null,
  });
  const [dataPerdidos, setDataPerdidos] = useState([]);
  const [loadingPerdidos, setLoadingPerdidos] = useState(false);

  // ── Abanderados y Escoltas ───────────────────────────────────────────────
  const [filtrosAbanderados, setFiltrosAbanderados] = useState({
    p_CicloEscolar: CICLO_ACTUAL,
    IdNivel: null,
    cantidadAbanderados: 5,
    cantidadEscoltas: null,   // null = dejar que el backend calcule 3×abanderados
    IdJornada: null,
    IdGrado: null,
    IdSeccion: null,
  });
  const [dataAbanderados, setDataAbanderados] = useState(null);   // null = sin búsqueda aún
  const [loadingAbanderados, setLoadingAbanderados] = useState(false);

  useEffect(() => { cargarCatalogos(); }, []);

  const cargarCatalogos = async () => {
    try {
      const [gradosRes, seccionesRes, jornadasRes, nivelesRes] = await Promise.all([
        apiClient.get('/grados'),
        apiClient.get('/secciones'),
        apiClient.get('/jornadas'),
        apiClient.get('/niveles'),
      ]);
      setGrados((gradosRes.data.data || gradosRes.data || []).filter(g => g.IdGrado != null));
      setSecciones((seccionesRes.data.data || seccionesRes.data || []).filter(s => s.IdSeccion != null));
      setJornadas((jornadasRes.data.data || jornadasRes.data || []).filter(j => j.IdJornada != null));
      setNiveles(nivelesRes.data.data || nivelesRes.data || []);
    } catch {
      message.error('Error al cargar catálogos');
    } finally {
      setLoadingCatalogos(false);
    }
  };

  // ── Búsquedas ────────────────────────────────────────────────────────────
  const buscarTop = async () => {
    setLoadingTop(true);
    try {
      const params = { p_CicloEscolar: filtrosTop.p_CicloEscolar, limite: filtrosTop.limite };
      if (filtrosTop.IdGrado)   params.IdGrado   = filtrosTop.IdGrado;
      if (filtrosTop.IdSeccion) params.IdSeccion  = filtrosTop.IdSeccion;
      if (filtrosTop.IdJornada) params.IdJornada  = filtrosTop.IdJornada;
      const res = await apiClient.get('/reportes/top-promedios', { params });
      setDataTop(res.data.data || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar top promedios');
    } finally {
      setLoadingTop(false);
    }
  };

  const buscarLimpio = async () => {
    setLoadingLimpio(true);
    try {
      const params = { p_CicloEscolar: filtrosLimpio.p_CicloEscolar };
      if (filtrosLimpio.IdNivel)   params.IdNivel   = filtrosLimpio.IdNivel;
      if (filtrosLimpio.IdGrado)   params.IdGrado   = filtrosLimpio.IdGrado;
      if (filtrosLimpio.IdSeccion) params.IdSeccion = filtrosLimpio.IdSeccion;
      if (filtrosLimpio.IdJornada) params.IdJornada = filtrosLimpio.IdJornada;
      const res = await apiClient.get('/reportes/alumnos-en-limpio', { params });
      setDataLimpio(res.data.data || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar alumnos en limpio');
    } finally {
      setLoadingLimpio(false);
    }
  };

  const buscarPerdidos = async () => {
    if (!filtrosPerdidos.IdGrado) {
      message.warning('Selecciona un grado para continuar');
      return;
    }
    setLoadingPerdidos(true);
    try {
      const params = { p_CicloEscolar: filtrosPerdidos.p_CicloEscolar, IdGrado: filtrosPerdidos.IdGrado };
      if (filtrosPerdidos.IdSeccion) params.IdSeccion = filtrosPerdidos.IdSeccion;
      if (filtrosPerdidos.IdJornada) params.IdJornada = filtrosPerdidos.IdJornada;
      const res = await apiClient.get('/reportes/alumnos-cursos-perdidos', { params });
      setDataPerdidos(res.data.data || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar alumnos con cursos perdidos');
    } finally {
      setLoadingPerdidos(false);
    }
  };

  const buscarAbanderados = async () => {
    if (!filtrosAbanderados.IdNivel) {
      message.warning('Selecciona un nivel para continuar');
      return;
    }
    if (!filtrosAbanderados.cantidadAbanderados || filtrosAbanderados.cantidadAbanderados < 1) {
      message.warning('Ingresa una cantidad válida de abanderados (mínimo 1)');
      return;
    }
    setLoadingAbanderados(true);
    try {
      const params = {
        p_CicloEscolar: filtrosAbanderados.p_CicloEscolar,
        IdNivel: filtrosAbanderados.IdNivel,
        cantidadAbanderados: filtrosAbanderados.cantidadAbanderados,
      };
      if (filtrosAbanderados.cantidadEscoltas !== null && filtrosAbanderados.cantidadEscoltas !== undefined)
        params.cantidadEscoltas = filtrosAbanderados.cantidadEscoltas;
      if (filtrosAbanderados.IdJornada)  params.IdJornada  = filtrosAbanderados.IdJornada;
      if (filtrosAbanderados.IdGrado)    params.IdGrado    = filtrosAbanderados.IdGrado;
      if (filtrosAbanderados.IdSeccion)  params.IdSeccion  = filtrosAbanderados.IdSeccion;
      const res = await apiClient.get('/reportes/abanderados-escoltas', { params });
      setDataAbanderados(res.data.data || { alumnos: [], totalEncontrados: 0 });
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar abanderados y escoltas');
    } finally {
      setLoadingAbanderados(false);
    }
  };

  // ── Exportar Excel ───────────────────────────────────────────────────────
  const exportarTop = () => {
    if (!dataTop.length) { message.warning('No hay datos para exportar'); return; }
    const filas = dataTop.map((r, i) => ({
      'Pos.': i + 1,
      Alumno: `${r.Apellidos} ${r.Nombres}`,
      Matrícula: r.Matricula,
      Grado: r.NombreGrado,
      Sección: r.NombreSeccion,
      Jornada: r.NombreJornada,
      'Promedio General': toNum(r.PromedioGeneral),
    }));
    exportarXLSX(filas, `Top_Promedios_${filtrosTop.p_CicloEscolar}`);
  };

  const exportarLimpio = () => {
    if (!dataLimpio.length) { message.warning('No hay datos para exportar'); return; }
    const filas = dataLimpio.map((r, i) => ({
      '#': i + 1,
      Alumno: `${r.Apellidos ?? ''} ${r.Nombres ?? ''}`.trim() || r.NombreCompleto,
      Matrícula: r.Matricula,
      Grado: r.NombreGrado,
      Sección: r.NombreSeccion,
      Jornada: r.NombreJornada,
      'Unidades Aprobadas': r.UnidadesAprobadas,
      'Unidades Reprobadas': r.UnidadesReprobadas,
    }));
    exportarXLSX(filas, `En_Limpio_${filtrosLimpio.p_CicloEscolar}`);
  };

  const exportarPerdidos = () => {
    if (!dataPerdidos.length) { message.warning('No hay datos para exportar'); return; }
    const filas = [];
    dataPerdidos.forEach(r => {
      (r.cursosPerdidos || []).forEach(c => {
        (c.unidadesReprobadas || []).forEach(u => {
          filas.push({
            Alumno: r.NombreCompleto,
            Matrícula: r.Matricula,
            Grado: r.NombreGrado,
            Sección: r.NombreSeccion,
            Jornada: r.NombreJornada,
            'Cursos perdidos': r.cuantosCursosPerdidos,
            Curso: c.NombreCurso,
            'Unidad reprobada': u.NumeroUnidad,
            Nota: u.NotaTotal,
          });
        });
      });
    });
    exportarXLSX(filas, `Cursos_Perdidos_${filtrosPerdidos.p_CicloEscolar}`);
  };

  const exportarAbanderados = () => {
    const lista = dataAbanderados?.alumnos || [];
    if (!lista.length) { message.warning('No hay datos para exportar'); return; }
    const filas = lista.map(r => ({
      'Posición': r.Posicion,
      'Tipo': r.Tipo,
      'Alumno': `${r.Apellidos} ${r.Nombres}`,
      'Matrícula': r.Matricula,
      'Grado': r.NombreGrado,
      'Sección': r.NombreSeccion,
      'Jornada': r.NombreJornada,
      'Promedio General': toNum(r.PromedioGeneral),
      'Unidades Aprobadas': r.UnidadesAprobadas,
    }));
    exportarXLSX(filas, `Abanderados_Escoltas_${filtrosAbanderados.p_CicloEscolar}`);
  };

  const exportarXLSX = (filas, nombre) => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filas);
    XLSX.utils.book_append_sheet(wb, ws, nombre.slice(0, 31));
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]),
      `${nombre}.xlsx`
    );
  };

  // ── Selectores reutilizables ─────────────────────────────────────────────
  const SelectCiclo = ({ value, onChange }) => (
    <Select style={{ width: 90 }} value={value} onChange={onChange}>
      {AÑOS.map(y => <Option key={y} value={y}>{y}</Option>)}
    </Select>
  );

  const SelectNivel = ({ value, onChange, required }) => (
    <Select
      style={{ width: 160 }}
      placeholder={required ? 'Nivel *' : 'Nivel'}
      allowClear={!required}
      value={value}
      onChange={onChange}
      loading={loadingCatalogos}
      status={required && !value ? 'warning' : ''}
    >
      {niveles.map(n => (
        <Option key={n.IdNivel} value={n.IdNivel}>{n.NombreNivel}</Option>
      ))}
    </Select>
  );

  const SelectGrado = ({ value, onChange, required }) => (
    <Select
      style={{ width: 180 }}
      placeholder={required ? 'Grado *' : 'Grado'}
      allowClear={!required}
      value={value}
      onChange={onChange}
      loading={loadingCatalogos}
      status={required && !value ? 'warning' : ''}
    >
      {grados.map(g => <Option key={g.IdGrado} value={g.IdGrado}>{g.NombreGrado}</Option>)}
    </Select>
  );

  const SelectSeccion = ({ value, onChange }) => (
    <Select style={{ width: 110 }} placeholder="Sección" allowClear value={value} onChange={onChange}
      loading={loadingCatalogos}>
      {secciones.map(s => <Option key={s.IdSeccion} value={s.IdSeccion}>{s.NombreSeccion}</Option>)}
    </Select>
  );

  const SelectJornada = ({ value, onChange }) => (
    <Select style={{ width: 130 }} placeholder="Jornada" allowClear value={value} onChange={onChange}
      loading={loadingCatalogos}>
      {jornadas.map(j => <Option key={j.IdJornada} value={j.IdJornada}>{j.NombreJornada}</Option>)}
    </Select>
  );

  // ── Columnas ─────────────────────────────────────────────────────────────
  const colsTop = [
    {
      title: 'Pos.', key: 'pos', width: 60, align: 'center',
      render: (_, __, i) => (
        i < 3
          ? <Tag color={['gold', 'silver', '#cd7f32'][i]} style={{ fontWeight: 'bold' }}>{i + 1}</Tag>
          : <Text type="secondary">{i + 1}</Text>
      ),
    },
    {
      title: 'Alumno', key: 'alumno', width: 220,
      sorter: (a, b) => `${a.Apellidos} ${a.Nombres}`.localeCompare(`${b.Apellidos} ${b.Nombres}`),
      render: (_, r) => <Text strong={toNum(r.PromedioGeneral) >= 90}>{r.Apellidos} {r.Nombres}</Text>,
    },
    { title: 'Matrícula', dataIndex: 'Matricula', key: 'mat', width: 110 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'grado', width: 160,
      sorter: (a, b) => a.NombreGrado.localeCompare(b.NombreGrado) },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'sec', width: 80, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'jornada', width: 110 },
    {
      title: 'Promedio General', dataIndex: 'PromedioGeneral', key: 'prom',
      width: 140, align: 'center', fixed: 'right',
      sorter: (a, b) => toNum(a.PromedioGeneral) - toNum(b.PromedioGeneral),
      render: (v) => <TagNota valor={v} style={{ fontSize: 14, fontWeight: 'bold' }} />,
    },
  ];

  const colsLimpio = [
    { title: '#', key: 'idx', width: 50, align: 'center', render: (_, __, i) => i + 1 },
    {
      title: 'Alumno', key: 'alumno', width: 220,
      sorter: (a, b) => {
        const na = `${a.Apellidos ?? ''} ${a.Nombres ?? ''}`.trim() || a.NombreCompleto || '';
        const nb = `${b.Apellidos ?? ''} ${b.Nombres ?? ''}`.trim() || b.NombreCompleto || '';
        return na.localeCompare(nb);
      },
      render: (_, r) => `${r.Apellidos ?? ''} ${r.Nombres ?? ''}`.trim() || r.NombreCompleto,
    },
    { title: 'Matrícula', dataIndex: 'Matricula', key: 'mat', width: 110 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'grado', width: 160 },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'sec', width: 80, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'jornada', width: 110 },
    {
      title: 'Unidades Aprobadas', dataIndex: 'UnidadesAprobadas', key: 'apr',
      width: 140, align: 'center',
      render: (v) => <Tag color="green">{v}</Tag>,
    },
    {
      title: 'Unidades Reprobadas', dataIndex: 'UnidadesReprobadas', key: 'rep',
      width: 150, align: 'center',
      render: (v) => <Tag color={Number(v) === 0 ? 'green' : 'red'}>{v}</Tag>,
    },
  ];

  const colsPerdidos = [
    { title: '#', key: 'idx', width: 50, align: 'center', render: (_, __, i) => i + 1 },
    {
      title: 'Alumno', dataIndex: 'NombreCompleto', key: 'alumno', width: 220,
      sorter: (a, b) => a.NombreCompleto.localeCompare(b.NombreCompleto),
    },
    { title: 'Matrícula', dataIndex: 'Matricula', key: 'mat', width: 110 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'grado', width: 160 },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'sec', width: 80, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'jornada', width: 110 },
    {
      title: 'Cursos Perdidos', dataIndex: 'cuantosCursosPerdidos', key: 'cant',
      width: 130, align: 'center',
      sorter: (a, b) => a.cuantosCursosPerdidos - b.cuantosCursosPerdidos,
      render: (v) => <Badge count={v} color="red" showZero />,
    },
  ];

  const colsAbanderados = [
    {
      title: 'Pos.', dataIndex: 'Posicion', key: 'pos', width: 65, align: 'center',
      render: (pos) => {
        if (pos === 1) return <Tag color="gold"  style={{ fontWeight: 'bold', fontSize: 13 }}>🥇 1</Tag>;
        if (pos === 2) return <Tag color="silver" style={{ fontWeight: 'bold', fontSize: 13 }}>🥈 2</Tag>;
        if (pos === 3) return <Tag color="#cd7f32" style={{ fontWeight: 'bold', fontSize: 13 }}>🥉 3</Tag>;
        return <Text type="secondary">{pos}</Text>;
      },
    },
    {
      title: 'Tipo', dataIndex: 'Tipo', key: 'tipo', width: 110, align: 'center',
      render: (tipo) => (
        tipo === 'Abanderado'
          ? <Tag color="gold" icon={<StarOutlined />} style={{ fontWeight: 'bold' }}>Abanderado</Tag>
          : <Tag color="blue">Escolta</Tag>
      ),
      filters: [
        { text: 'Abanderado', value: 'Abanderado' },
        { text: 'Escolta', value: 'Escolta' },
      ],
      onFilter: (value, record) => record.Tipo === value,
    },
    {
      title: 'Alumno', key: 'alumno', width: 220,
      render: (_, r) => (
        <Text strong={r.Tipo === 'Abanderado'}>{r.Apellidos} {r.Nombres}</Text>
      ),
    },
    { title: 'Matrícula', dataIndex: 'Matricula', key: 'mat', width: 110 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'grado', width: 160 },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'sec', width: 80, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'jornada', width: 110 },
    {
      title: 'Promedio General', dataIndex: 'PromedioGeneral', key: 'prom',
      width: 140, align: 'center', fixed: 'right',
      render: (v) => <TagNota valor={v} style={{ fontSize: 14, fontWeight: 'bold' }} />,
    },
  ];

  // Fila expandida para cursos perdidos
  const expandedRow = (record) => (
    <div style={{ padding: '8px 16px' }}>
      {(record.cursosPerdidos || []).map((curso, ci) => (
        <Card
          key={ci}
          size="small"
          title={<Text type="danger"><WarningOutlined /> {curso.NombreCurso}</Text>}
          style={{ marginBottom: 8 }}
          bodyStyle={{ padding: '8px 12px' }}
        >
          <Row gutter={8}>
            {(curso.unidadesReprobadas || []).map((u, ui) => (
              <Col key={ui}>
                <Space>
                  <Text type="secondary">Unidad {u.NumeroUnidad}:</Text>
                  <Tag color="red">{u.NotaTotal} pts</Tag>
                </Space>
              </Col>
            ))}
          </Row>
        </Card>
      ))}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  const tabItems = [
    // ── Tab 1: Top Promedios ────────────────────────────────────────────────
    {
      key: 'top',
      label: <span><TrophyOutlined /> Top Promedios</span>,
      children: (
        <>
          <Alert
            type="info" showIcon
            message="Ranking de mejores promedios generales de la institución. El promedio es calculado sobre todas las unidades del alumno."
            style={{ marginBottom: 16 }}
          />
          <Space wrap style={{ marginBottom: 16 }}>
            <SelectCiclo
              value={filtrosTop.p_CicloEscolar}
              onChange={v => setFiltrosTop(f => ({ ...f, p_CicloEscolar: v }))}
            />
            <SelectGrado
              value={filtrosTop.IdGrado}
              onChange={v => setFiltrosTop(f => ({ ...f, IdGrado: v, IdSeccion: null }))}
            />
            <SelectSeccion
              value={filtrosTop.IdSeccion}
              onChange={v => setFiltrosTop(f => ({ ...f, IdSeccion: v }))}
            />
            <SelectJornada
              value={filtrosTop.IdJornada}
              onChange={v => setFiltrosTop(f => ({ ...f, IdJornada: v }))}
            />
            <Tooltip title="Cantidad máxima de resultados">
              <InputNumber
                min={1} max={200} value={filtrosTop.limite}
                onChange={v => setFiltrosTop(f => ({ ...f, limite: v }))}
                prefix="Top" style={{ width: 100 }}
              />
            </Tooltip>
            <Button type="primary" icon={<SearchOutlined />} onClick={buscarTop} loading={loadingTop}>
              Buscar
            </Button>
            <Tooltip title="Exportar a Excel">
              <Button icon={<DownloadOutlined />} onClick={exportarTop} disabled={!dataTop.length}>
                Excel
              </Button>
            </Tooltip>
          </Space>

          <Table
            columns={colsTop}
            dataSource={dataTop}
            rowKey={(_, i) => `top-${i}`}
            loading={loadingTop}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `Total: ${t} alumnos` }}
            bordered size="middle" scroll={{ x: 950 }}
            locale={{ emptyText: 'Configura los filtros y presiona Buscar' }}
            rowClassName={(_, i) => i < 3 ? 'row-podio' : ''}
          />
        </>
      ),
    },

    // ── Tab 2: En Limpio ────────────────────────────────────────────────────
    {
      key: 'limpio',
      label: <span><CheckCircleOutlined /> Estudiantes en Limpio</span>,
      children: (
        <>
          <Alert
            type="success" showIcon
            message="Alumnos que no han reprobado ninguna unidad de ningún curso. Se usa para reconocimientos institucionales."
            description={<Text type="secondary">Filtra por grado, sección o jornada — o deja los filtros vacíos para ver toda la institución. Un alumno sin notas registradas no aparece en este listado.</Text>}
            style={{ marginBottom: 16 }}
          />
          <Space wrap style={{ marginBottom: 16 }}>
            <SelectCiclo
              value={filtrosLimpio.p_CicloEscolar}
              onChange={v => setFiltrosLimpio(f => ({ ...f, p_CicloEscolar: v }))}
            />
            <SelectNivel
              value={filtrosLimpio.IdNivel}
              onChange={v => setFiltrosLimpio(f => ({ ...f, IdNivel: v }))}
            />
            <SelectGrado
              value={filtrosLimpio.IdGrado}
              onChange={v => setFiltrosLimpio(f => ({ ...f, IdGrado: v, IdSeccion: null }))}
            />
            <SelectSeccion
              value={filtrosLimpio.IdSeccion}
              onChange={v => setFiltrosLimpio(f => ({ ...f, IdSeccion: v }))}
            />
            <SelectJornada
              value={filtrosLimpio.IdJornada}
              onChange={v => setFiltrosLimpio(f => ({ ...f, IdJornada: v }))}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={buscarLimpio} loading={loadingLimpio}>
              Buscar
            </Button>
            <Tooltip title="Exportar a Excel">
              <Button icon={<DownloadOutlined />} onClick={exportarLimpio} disabled={!dataLimpio.length}>
                Excel
              </Button>
            </Tooltip>
          </Space>

          <Table
            columns={colsLimpio}
            dataSource={dataLimpio}
            rowKey={(r, i) => r.IdAlumno ?? `lim-${i}`}
            loading={loadingLimpio}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `Total: ${t} alumnos` }}
            bordered size="middle" scroll={{ x: 950 }}
            locale={{ emptyText: 'Configura los filtros y presiona Buscar' }}
          />
        </>
      ),
    },

    // ── Tab 3: Cursos Perdidos ──────────────────────────────────────────────
    {
      key: 'perdidos',
      label: <span><WarningOutlined /> Estudiantes con Cursos Perdidos</span>,
      children: (
        <>
          <Alert
            type="warning" showIcon
            message="Alumnos que reprobaron al menos una unidad de algún curso."
            description={
              <Text type="secondary">
                Una unidad reprobada es nota inferior a 60. Un alumno puede tener buen promedio final y aún así aparecer aquí si reprobó una unidad individual. Expande cada fila para ver el detalle por curso.
              </Text>
            }
            style={{ marginBottom: 16 }}
          />
          <Space wrap style={{ marginBottom: 16 }}>
            <SelectCiclo
              value={filtrosPerdidos.p_CicloEscolar}
              onChange={v => setFiltrosPerdidos(f => ({ ...f, p_CicloEscolar: v }))}
            />
            <SelectGrado
              value={filtrosPerdidos.IdGrado}
              required
              onChange={v => setFiltrosPerdidos(f => ({ ...f, IdGrado: v, IdSeccion: null }))}
            />
            <SelectSeccion
              value={filtrosPerdidos.IdSeccion}
              onChange={v => setFiltrosPerdidos(f => ({ ...f, IdSeccion: v }))}
            />
            <SelectJornada
              value={filtrosPerdidos.IdJornada}
              onChange={v => setFiltrosPerdidos(f => ({ ...f, IdJornada: v }))}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={buscarPerdidos} loading={loadingPerdidos}>
              Buscar
            </Button>
            <Tooltip title="Exportar a Excel (una fila por unidad reprobada)">
              <Button icon={<DownloadOutlined />} onClick={exportarPerdidos} disabled={!dataPerdidos.length}>
                Excel
              </Button>
            </Tooltip>
          </Space>

          <Table
            columns={colsPerdidos}
            dataSource={dataPerdidos}
            rowKey={(r, i) => r.IdAlumno ?? `per-${i}`}
            loading={loadingPerdidos}
            expandable={{
              expandedRowRender: expandedRow,
              rowExpandable: r => (r.cursosPerdidos || []).length > 0,
            }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `Total: ${t} alumnos` }}
            bordered size="middle" scroll={{ x: 950 }}
            locale={{ emptyText: 'Selecciona un grado y presiona Buscar' }}
          />
        </>
      ),
    },

    // ── Tab 4: Abanderados y Escoltas ───────────────────────────────────────
    {
      key: 'abanderados',
      label: <span><StarOutlined /> Abanderados y Escoltas</span>,
      children: (
        <>
          <Alert
            type="info" showIcon
            message="Reporte para acto de graduación o fin de ciclo. Muestra los N mejores promedios del nivel seleccionado que estén en limpio (sin ningún curso reprobado)."
            description={
              <Text type="secondary">
                Los primeros son <strong>Abanderados</strong>; los siguientes son <strong>Escoltas</strong>.
                Si no se especifica cantidad de escoltas, el backend usa 3 × abanderados por defecto.
              </Text>
            }
            style={{ marginBottom: 16 }}
          />

          <Space wrap style={{ marginBottom: 16 }}>
            <SelectCiclo
              value={filtrosAbanderados.p_CicloEscolar}
              onChange={v => setFiltrosAbanderados(f => ({ ...f, p_CicloEscolar: v }))}
            />
            <SelectNivel
              required
              value={filtrosAbanderados.IdNivel}
              onChange={v => setFiltrosAbanderados(f => ({ ...f, IdNivel: v }))}
            />
            <Tooltip title="Cantidad de abanderados (requerido)">
              <InputNumber
                min={1} max={50}
                value={filtrosAbanderados.cantidadAbanderados}
                onChange={v => setFiltrosAbanderados(f => ({ ...f, cantidadAbanderados: v }))}
                addonBefore="Abanderados"
                style={{ width: 170 }}
                placeholder="5"
              />
            </Tooltip>
            <Tooltip title="Cantidad de escoltas. Si se deja vacío, el sistema usa 3 × abanderados">
              <InputNumber
                min={0} max={200}
                value={filtrosAbanderados.cantidadEscoltas}
                onChange={v => setFiltrosAbanderados(f => ({ ...f, cantidadEscoltas: v }))}
                addonBefore="Escoltas"
                style={{ width: 155 }}
                placeholder="automático"
              />
            </Tooltip>
          </Space>

          <Space wrap style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Filtros opcionales:</Text>
            <SelectJornada
              value={filtrosAbanderados.IdJornada}
              onChange={v => setFiltrosAbanderados(f => ({ ...f, IdJornada: v }))}
            />
            <SelectGrado
              value={filtrosAbanderados.IdGrado}
              onChange={v => setFiltrosAbanderados(f => ({ ...f, IdGrado: v, IdSeccion: null }))}
            />
            <SelectSeccion
              value={filtrosAbanderados.IdSeccion}
              onChange={v => setFiltrosAbanderados(f => ({ ...f, IdSeccion: v }))}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={buscarAbanderados}
              loading={loadingAbanderados}
            >
              Generar reporte
            </Button>
            <Tooltip title="Exportar a Excel">
              <Button
                icon={<DownloadOutlined />}
                onClick={exportarAbanderados}
                disabled={!dataAbanderados?.alumnos?.length}
              >
                Excel
              </Button>
            </Tooltip>
          </Space>

          {/* Resumen de resultados — usa los valores reales del backend */}
          {dataAbanderados && (() => {
            const pedidosTotal = (dataAbanderados.cantidadAbanderadosPedida || 0) +
              (dataAbanderados.cantidadEscoltasPedida || 0);
            const encontrados  = dataAbanderados.totalEncontrados || 0;
            const nAbanderados = (dataAbanderados.alumnos || []).filter(a => a.Tipo === 'Abanderado').length;
            const nEscoltas    = (dataAbanderados.alumnos || []).filter(a => a.Tipo === 'Escolta').length;

            if (encontrados === 0) return (
              <Alert
                type="warning" showIcon
                message="No se encontraron alumnos en limpio con los filtros seleccionados."
                style={{ marginBottom: 16 }}
              />
            );

            const incompleto = encontrados < pedidosTotal;
            return (
              <Alert
                type={incompleto ? 'warning' : 'success'}
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  <Space size={24}>
                    <span>🏆 <strong>{nAbanderados}</strong> Abanderado{nAbanderados !== 1 ? 's' : ''}</span>
                    <span>🔵 <strong>{nEscoltas}</strong> Escolta{nEscoltas !== 1 ? 's' : ''}</span>
                    {incompleto && (
                      <Text type="warning">
                        (se solicitaron {pedidosTotal} en total — no había suficientes alumnos en limpio)
                      </Text>
                    )}
                  </Space>
                }
              />
            );
          })()}

          <Table
            columns={colsAbanderados}
            dataSource={dataAbanderados?.alumnos || []}
            rowKey={(r) => r.IdAlumno}
            loading={loadingAbanderados}
            pagination={false}
            bordered
            size="middle"
            scroll={{ x: 1000 }}
            locale={{ emptyText: 'Selecciona un nivel y presiona Generar reporte' }}
            rowClassName={(r) => r.Tipo === 'Abanderado' ? 'row-abanderado' : 'row-escolta'}
          />
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title={<span><TrophyOutlined /> Rendimiento Estudiantil</span>}>
        <Tabs activeKey={tab} onChange={setTab} items={tabItems} />
      </Card>
      <style>{`
        .row-abanderado { background-color: #fffbe6; }
        .row-abanderado:hover td { background-color: #fff7cc !important; }
        .row-podio { background-color: #f6ffed; }
        .row-podio:hover td { background-color: #d9f7be !important; }
      `}</style>
    </div>
  );
};

export default RendimientoEstudiantil;
